import * as THREE from 'three';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { EventEmitter } from '@/core/EventEmitter';
import { createLogger } from '@/utils/logger';
import type { LoaderFactory } from './LoaderFactory';
import type { AssetEntry, AssetManifest } from '@/types';

// Type alias (not interface) so it carries the implicit index signature that
// EventEmitter's `Record<string, unknown>` constraint requires.
type AssetEvents = {
  progress: { loaded: number; total: number; fraction: number; url: string };
  loaded: { name: string };
  error: { name: string; error: unknown };
  ready: { count: number };
};

/**
 * Manifest-driven asynchronous asset loading. Loads everything in parallel,
 * tags color spaces, surfaces progress, and stores results for typed retrieval.
 * Optional assets that fail to load are skipped (graceful degradation).
 */
export class AssetManager extends EventEmitter<AssetEvents> {
  private readonly store = new Map<string, unknown>();
  private readonly log = createLogger('assets');
  /** True while the shared manager has items in flight (see `whenIdle`). */
  private busy = false;
  private readonly idleWaiters: Array<() => void> = [];

  /**
   * Resolve once the shared `LoadingManager` has nothing in flight.
   *
   * Needed because the manifest is NOT the whole picture (2026-07-19, user: "make sure in
   * preloading only you're loading all the assets"). The baked-atlas resolver kicks off its
   * texture loads from inside `World.build()`, i.e. AFTER `loadAll` has already resolved — so
   * without this the preloader finished while the scene's actual surfaces were still downloading
   * and the architecture popped in untextured. Awaiting this before `Preloader.finish()` makes
   * the gate mean what it says.
   *
   * `timeoutMs` is a safety valve: one wedged request must never strand the preloader (the
   * failure mode the user hit at 92%).
   */
  whenIdle(timeoutMs = 20000): Promise<void> {
    if (!this.busy) return Promise.resolve();
    return new Promise<void>((resolve) => {
      let settled = false;
      const done = (): void => {
        if (settled) return;
        settled = true;
        resolve();
      };
      this.idleWaiters.push(done);
      window.setTimeout(() => {
        if (!settled) this.log.warn('whenIdle timed out — continuing without the stragglers.');
        done();
      }, timeoutMs);
    });
  }

  /** The shared `LoadingManager` every loader reports through. Exposed so systems that build
   *  their OWN loaders (e.g. the baked-atlas resolver in `orvaeScene`) can join the SAME
   *  progress accounting instead of silently binding to three's default manager — otherwise
   *  their textures load outside the preloader and the scene pops in after the gate. */
  get loadingManager(): THREE.LoadingManager {
    return this.loaders.manager;
  }

  constructor(private readonly loaders: LoaderFactory) {
    super();
    this.loaders.manager.onProgress = (url, loaded, total) => {
      this.busy = true;
      this.emit('progress', { url, loaded, total, fraction: total ? loaded / total : 0 });
    };
    // Track idle/busy so `whenIdle` can wait out loads started AFTER `loadAll` — see below.
    const manager = this.loaders.manager;
    const prevStart = manager.onStart;
    manager.onStart = (url, loaded, total) => {
      this.busy = true;
      prevStart?.(url, loaded, total);
    };
    const prevLoad = manager.onLoad;
    manager.onLoad = () => {
      this.busy = false;
      const waiters = this.idleWaiters.splice(0);
      for (const resolve of waiters) resolve();
      prevLoad?.();
    };
  }

  async loadAll(manifest: AssetManifest): Promise<void> {
    await Promise.all(manifest.map((entry) => this.loadEntry(entry)));
    this.emit('ready', { count: this.store.size });
  }

  private async loadEntry(entry: AssetEntry): Promise<void> {
    try {
      this.store.set(entry.name, await this.loadByType(entry));
      this.emit('loaded', { name: entry.name });
    } catch (error) {
      this.emit('error', { name: entry.name, error });
      if (!entry.optional) throw error;
      this.log.warn(`optional asset "${entry.name}" failed (${entry.path}) — continuing.`);
    }
  }

  private async loadByType(entry: AssetEntry): Promise<unknown> {
    switch (entry.type) {
      case 'gltf':
        return this.loaders.gltf.loadAsync(entry.path);
      case 'texture': {
        const texture = await this.loaders.texture.loadAsync(entry.path);
        return this.configureTexture(texture, entry);
      }
      case 'hdr': {
        const hdr = await this.loaders.rgbe.loadAsync(entry.path);
        hdr.mapping = THREE.EquirectangularReflectionMapping;
        return hdr;
      }
      case 'audio':
        // Howler manages its own decoding; we just surface the resolved path.
        return entry.path;
      default:
        throw new Error(`Unsupported asset type: ${String(entry.type)}`);
    }
  }

  private configureTexture(texture: THREE.Texture, entry: AssetEntry): THREE.Texture {
    texture.colorSpace = entry.colorSpace === 'linear' ? THREE.NoColorSpace : THREE.SRGBColorSpace;
    texture.flipY = entry.flipY ?? false; // baked-map convention by default
    texture.needsUpdate = true;
    return texture;
  }

  has(name: string): boolean {
    return this.store.has(name);
  }

  get<T>(name: string): T | undefined {
    return this.store.get(name) as T | undefined;
  }

  getGLTF(name: string): GLTF | undefined {
    return this.store.get(name) as GLTF | undefined;
  }

  getTexture(name: string): THREE.Texture | undefined {
    return this.store.get(name) as THREE.Texture | undefined;
  }

  dispose(): void {
    this.store.clear();
    this.clearListeners();
  }
}
